import { useState, useCallback } from "react";
import ChatPanel from "../components/ChatPanel";
import GeneratePanel from "../components/GeneratePanel";
import * as api from "../api";
import { useJobs } from "../jobs";

/* Human-readable labels per generatable artifact / workflow. */
const GEN_LABELS = {
  briefing:    "Research Brief",
  flashcards:  "Flashcards",
  study_guide: "Study Guide",
  timeline:    "Timeline",
  mind_map:    "Mind Map",
  formulas:    "Formula Sheet",
  quiz:        "Quiz",
  agent:       "AI Agent Workflow",
  study_plan:  "Study Plan",
};

export default function WorkspacePage({
  activeId,
  activeNotebook,
  isMobile,
  messages,
  setMessages,
  onPersistMessages,
  onUpload,
  uploading,
  outputs,
  onAddOutput,
  onProcessingChange,
  tutorMode, setTutorMode,
  socraticMode, setSocraticMode,
  quizCount, setQuizCount,
  weakAreas,
}) {
  const [sending, setSending] = useState(false);
  /* On mobile only one panel is visible at a time (screen too narrow for both). */
  const [mobileView, setMobileView] = useState("chat"); // "chat" | "tools"
  const { startJob, getJob } = useJobs();

  /* Generation runs as a global background job (kind "generate", scoped per
   * notebook) so it keeps processing even if the user navigates away. When it
   * finishes the result is appended to this notebook's output history. */
  const genJob = getJob("generate", activeId || "");
  const generating = genJob?.status === "running" ? genJob.meta?.type : null;

  const runGeneration = useCallback((type, label, task) => {
    if (!activeNotebook) return;
    const nbId = activeNotebook.id;
    startJob({
      kind: "generate",
      scopeId: nbId,
      label: `${label} · ${activeNotebook.title}`,
      doneLabel: label,
      meta: { type },
      run: task,
      onDone: (result) => onAddOutput?.(nbId, result),
    });
  }, [activeNotebook, startJob, onAddOutput]);

  /* ── Chat ────────────────────────────────────────────────────────────────── */
  const handleSend = useCallback(async (text) => {
    if (!activeNotebook || sending) return;

    const nbId = activeNotebook.id;
    const mode = socraticMode ? "socratic" : "";
    const userMsg = { role: "user", content: text };
    const aiMsg = { role: "assistant", content: "", streaming: true, mode: socraticMode ? "socratic" : null };

    setMessages(prev => [...prev, userMsg, aiMsg]);
    setSending(true);
    onProcessingChange?.(true, "Thinking…");

    try {
      const history = messages.map(m => ({ role: m.role, content: m.content }));

      if (api.chatWithNotebookStream) {
        // Streaming path
        let buffer = "";
        await api.chatWithNotebookStream(
          activeNotebook.id,
          text,
          history,
          tutorMode,
          socraticMode,
          (chunk) => {
            buffer += chunk;
            setMessages(prev => {
              const updated = [...prev];
              const last = updated[updated.length - 1];
              if (last?.role === "assistant") {
                updated[updated.length - 1] = { ...last, content: buffer, streaming: true };
              }
              return updated;
            });
          }
        );
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === "assistant") {
            updated[updated.length - 1] = { ...last, streaming: false };
          }
          return updated;
        });
        onPersistMessages?.(nbId, [
          userMsg,
          { role: "assistant", content: buffer, mode },
        ]);
      } else {
        // Non-streaming fallback
        const res = await api.chatWithNotebook(
          activeNotebook.id, text, history, tutorMode, socraticMode
        );
        const answer = res.response || "";
        const citations = res.citations || [];
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === "assistant") {
            updated[updated.length - 1] = {
              ...last,
              content: answer,
              citations,
              streaming: false,
            };
          }
          return updated;
        });
        onPersistMessages?.(nbId, [
          userMsg,
          { role: "assistant", content: answer, citations, mode },
        ]);
      }
    } catch {
      setMessages(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last?.role === "assistant") {
          updated[updated.length - 1] = {
            ...last,
            content: "⚠️ Something went wrong. Please try again.",
            streaming: false,
          };
        }
        return updated;
      });
    } finally {
      setSending(false);
      onProcessingChange?.(false);
    }
  }, [activeNotebook, sending, messages, tutorMode, socraticMode, setMessages, onProcessingChange, onPersistMessages]);

  /* ── Generate (study assets) ─────────────────────────────────────────────── */
  const handleGenerate = useCallback((artifactType) => {
    if (generating) return;
    const label = GEN_LABELS[artifactType] || artifactType.replace("_", " ");
    runGeneration(artifactType, label, async (signal) => {
      const res = await api.generateArtifact(
        activeNotebook.id, artifactType, quizCount, "", tutorMode, signal
      );
      return { type: artifactType, content: res.content || res.response || "" };
    });
  }, [activeNotebook, generating, quizCount, tutorMode, runGeneration]);

  /* ── Agent ───────────────────────────────────────────────────────────────── */
  const handleAgent = useCallback(() => {
    if (generating) return;
    runGeneration("agent", GEN_LABELS.agent, async (signal) => {
      const res = await api.generateAgentWorkflow(
        activeNotebook.id, "Prepare full exam prep", "", tutorMode, "", signal
      );
      return { type: "agent", content: res.content || res.response || "" };
    });
  }, [activeNotebook, generating, tutorMode, runGeneration]);

  /* ── Study Plan ──────────────────────────────────────────────────────────── */
  const handleStudyPlan = useCallback(() => {
    if (generating) return;
    runGeneration("study_plan", GEN_LABELS.study_plan, async (signal) => {
      const res = await api.generateStudyPlan(activeNotebook.id, "", 2.0, [], signal);
      return { type: "study_plan", content: res.content || res.response || "" };
    });
  }, [activeNotebook, generating, runGeneration]);

  const showChat = !isMobile || mobileView === "chat";
  const showTools = !isMobile || mobileView === "tools";

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      {isMobile && (
        <div
          style={{
            display: "flex",
            gap: 4,
            padding: 6,
            background: "var(--surface)",
            borderBottom: "1px solid var(--border)",
            flexShrink: 0,
          }}
        >
          {[
            { id: "chat", icon: "chat", label: "Chat" },
            { id: "tools", icon: "auto_awesome", label: "Study Tools" },
          ].map((t) => {
            const active = mobileView === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setMobileView(t.id)}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "8px 10px",
                  borderRadius: "var(--radius-md)",
                  fontSize: 13,
                  fontWeight: 600,
                  color: active ? "var(--ink)" : "var(--ink-3)",
                  background: active ? "var(--surface-3)" : "transparent",
                  border: "1px solid",
                  borderColor: active ? "var(--border-2)" : "transparent",
                }}
              >
                <span
                  className="material-symbols-rounded"
                  style={{
                    fontSize: 17,
                    color: active ? "var(--accent-2)" : "inherit",
                    fontVariationSettings: active ? "'FILL' 1" : "'FILL' 0",
                  }}
                >
                  {t.icon}
                </span>
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ flex: 1, display: "flex", overflow: "hidden", minWidth: 0 }}>
        {showChat && (
          <ChatPanel
            notebook={activeNotebook}
            messages={messages}
            sending={sending}
            tutorMode={tutorMode}
            socraticMode={socraticMode}
            onSend={handleSend}
            onUpload={onUpload}
            uploading={uploading}
            weakAreas={weakAreas}
          />
        )}
        {showTools && (
          <GeneratePanel
            notebook={activeNotebook}
            isMobile={isMobile}
            generating={generating}
            onGenerate={handleGenerate}
            onAgent={handleAgent}
            onStudyPlan={handleStudyPlan}
            outputs={outputs}
            tutorMode={tutorMode}
            setTutorMode={setTutorMode}
            socraticMode={socraticMode}
            setSocraticMode={setSocraticMode}
            quizCount={quizCount}
            setQuizCount={setQuizCount}
          />
        )}
      </div>
    </div>
  );
}